import { SpeechRecognitionService } from './../../../core/services/speech-recognition.service';
import { GroupService } from './../../../core/services/group.service';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../../core/services/user.service';
import { PatientService } from './../../../core/services/patient.service';
import { ResultsService } from './../../../core/services/results.service';
import { InboxService } from './../../../core/services/inbox.service';
import { ChatService } from '../../../chat/chat.service';
import { ChatUiService } from '../../../chat/chat-ui.service';
import { InboxCounts } from './../../../core/models/inbox-item.model';
import { Subscription } from 'rxjs';
import Fuse, { Expression } from 'fuse.js';
import { TutorialService } from 'src/app/core/tutorial/tutorial.service';
import { HelpIndexDialogComponent } from '../help-index-dialog/help-index-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { isConnectEnabledForGroup } from '../../util/connect.util';
import { isStateModeGroup } from '../../util/state-mode.util';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  // A tooltip's default touch handling sets touch-action: none on its item, so on a phone a swipe that starts on an
  // item would not scroll the menu. Each item already shows its name.
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { showDelay: 0, hideDelay: 0, touchendHideDelay: 1500, touchGestures: 'off' } }],
})
export class SidenavComponent implements OnInit, OnDestroy {
  userRole?: 'Admin' | 'Physician' | 'Support-Limited' | 'Support-Unlimited';
  @Output() closeSidenav = new EventEmitter<void>();
  collapsed = false;
  isMobile = false;
  selectedGroup?: any;
  private subscriptions: Subscription[] = [];
  isRecognitionSupported: boolean = false;
  isRecognitionActive: boolean = false;
  detectedText: string = '';
  /*
   * The Inbox badge. TWO numbers, not four: `New` is work nobody has looked at and `For Review` is
   * work waiting on a clinician, and both are things a practice wants to see without opening the
   * page. `Flagged` and `Pending` are states somebody already knows about.
   *
   * These come from ONE `/counts` request per group change — four `COUNT(*)`s server-side — and
   * NOT from filtering a fully-loaded list the way the three below do. See `tasks/inbox.md` §6.
   */
  inboxNewCount: number = 0;
  inboxForReviewCount: number = 0;

  newResultsCount: number = 0;
  failedResultsCount: number = 0;
  unlinkedResultsCount: number = 0;
  canSeeFinancials: boolean = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private userService: UserService,
    private router: Router,
    private patientService: PatientService,
    private groupService: GroupService,
    private resultsService: ResultsService,
    private inboxService: InboxService,
    readonly chatService: ChatService,
    readonly chatUi: ChatUiService,
    private speechRecognitionService: SpeechRecognitionService,
    private tutorialService: TutorialService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef
  ) {

    this.groupService.selectedGroup$
      .subscribe(result => {
        this.selectedGroup = result;
      });


    this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape, Breakpoints.TabletPortrait])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
  }

  ngOnInit(): void {

    // Check if the device supports speech recognition
    this.isRecognitionSupported = this.speechRecognitionService.isSpeechRecognitionSupported();

    // Subscribe to speech recognition active status
    this.subscriptions.push(
      this.speechRecognitionService.isActive().subscribe((isActive) => {
        this.isRecognitionActive = isActive;
      })
    );

    // Subscribe to detected text from speech recognition
    this.subscriptions.push(
      this.speechRecognitionService.getDetectedText().subscribe((text) => {
        this.detectedText = text;
      })
    );

    // Subscribe to detected commands from speech recognition
    this.subscriptions.push(
      this.speechRecognitionService.getDetectedCommand().subscribe((commandData) => {
        this.handleCommand(commandData.command, commandData.payload);
      })
    );

    this.subscriptions.push(
      this.groupService.groupUsers$
        .subscribe(
          (results) => {
            if (results) {
              const currentGroupUser = results?.filter((u: any) => u.userId === this.userService.user.id);
              this.userRole = currentGroupUser[0].userRole;
              this.canSeeFinancials = currentGroupUser[0].canSeeFinancials || false;
            }
            this.cd.detectChanges();
          },
          (error) => {
            console.error('Error fetching group users:', error);
          }
        )
    );

    // Subscribe to results to update badge counts
    this.subscriptions.push(
      this.resultsService.results$
        .subscribe((results: any[]) => {
          if (results) {
            // Count new results (completed & unseen)
            this.newResultsCount = results.filter(order =>
              (order.status === 'completed' || order.status === 'cancelled') &&
              !order.reviewedAt
            ).length;

            // Count failed results
            this.failedResultsCount = results.filter(order =>
              order.status === 'failed'
            ).length;

            this.cd.detectChanges();
          }
        })
    );

    // The Inbox. Already summed server-side, so this only unpacks it — contrast the two blocks
    // above, which filter the whole practice's lab orders in the browser on every emission.
    this.subscriptions.push(
      this.inboxService.counts$
        .subscribe((counts: InboxCounts) => {
          this.inboxNewCount = counts ? counts['new'] : 0;
          this.inboxForReviewCount = counts ? counts['for-review'] : 0;
          this.cd.detectChanges();
        })
    );

    // Unlinked results: inbound pathology with no MedicAI lab order behind it.
    this.subscriptions.push(
      this.resultsService.unlinked$
        .subscribe((unlinked: any[]) => {
          this.unlinkedResultsCount = unlinked?.length || 0;
          this.cd.detectChanges();
        })
    );

  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /** The Connect item shows only for groups on MedicAI Connect. */
  get isConnectGroup(): boolean {
    return isConnectEnabledForGroup(this.selectedGroup);
  }

  /** A state unit (tasks/state-mode.md) records ICD-10 codes only: no Coding page. */
  get showCoding(): boolean {
    return !isStateModeGroup(this.selectedGroup);
  }

  get canViewStatistics() {
    return this.userRole?.toLowerCase() === 'admin' || this.canSeeFinancials;
  }

  startSpeechRecognition(): void {
    this.speechRecognitionService.startRecognition();
  }

  stopSpeechRecognition(): void {
    this.speechRecognitionService.stopRecognition();
  }

  private handleCommand(command: string, payload?: any): void {
    switch (command) {
      case 'GO_TO':
        if (payload) {
          this.router.navigate(['/patients', payload]);
        }
        break;
    }
  }

  onLogout() {
    // Emit before redirecting: `logoutAndRedirect()` tears the document down, so anything queued
    // after it is running on borrowed time.
    this.closeSidenav.emit();
    // One navigation to a fresh /login — see `UserService.logoutAndRedirect`.
    this.userService.logoutAndRedirect();
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  generatedDemoData() {
    this.patientService.seedSamplePatients(50)
      .subscribe()
  }

  closeOnMobile() {
    if (this.isMobile) {
      this.closeSidenav.emit();
    }
  }

  /** Unread messages from MedicAI Support, badged on Help. A getter: constructor parameters are assigned after fields. */
  get supportUnread$() {
    return this.chatService.supportUnread$;
  }

  openHelpDialog(): void {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.dialog.open(HelpIndexDialogComponent, {
      width: '400px',
      disableClose: false,
      panelClass: 'custom-dialog-container',
    })
    console.log("Opening the dialog")
    // this.tutorialService.closeTutorial();
  }


}